import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ContactWithDetails } from '../contacts/types';
import { contactsApi } from '../contacts/services/contactApi';
import { CategoryDropdown } from '../category';
import '../../../components/common/SearchSection.css';
import './ExpenseVendorContactSection.css';
import '../contacts/ContactSearchSection.css';

interface ExpenseVendorContactSectionProps {
  selectedContact: ContactWithDetails | null;
  onContactSelect: (contact: ContactWithDetails | null) => void;
  newContactBusinessName: string;
  newContactTypeId: number;
  onNewContactBusinessNameChange: (value: string) => void;
  onNewContactTypeIdChange: (value: number) => void;
  onNewContactModeChange: (active: boolean) => void;
  label?: string;
  placeholder?: string;
  errors?: {
    typeId?: string;
  };
}

const ExpenseVendorContactSection: React.FC<ExpenseVendorContactSectionProps> = ({
  selectedContact,
  onContactSelect,
  newContactTypeId,
  onNewContactBusinessNameChange,
  onNewContactTypeIdChange,
  onNewContactModeChange,
  label = 'Vendor',
  placeholder = 'Search vendors by first name, last name, or business name...',
  errors = {}
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<ContactWithDetails[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchedTerm, setSearchedTerm] = useState('');
  const [showContactType, setShowContactType] = useState(false);

  const onNewContactBusinessNameChangeRef = useRef(onNewContactBusinessNameChange);
  const onNewContactTypeIdChangeRef = useRef(onNewContactTypeIdChange);
  const onNewContactModeChangeRef = useRef(onNewContactModeChange);

  useEffect(() => {
    onNewContactBusinessNameChangeRef.current = onNewContactBusinessNameChange;
  }, [onNewContactBusinessNameChange]);

  useEffect(() => {
    onNewContactTypeIdChangeRef.current = onNewContactTypeIdChange;
  }, [onNewContactTypeIdChange]);

  useEffect(() => {
    onNewContactModeChangeRef.current = onNewContactModeChange;
  }, [onNewContactModeChange]);

  const trimmedTerm = searchTerm.trim();
  const noMatchFound = trimmedTerm !== ''
    && !searchLoading
    && searchedTerm === trimmedTerm
    && searchResults.length === 0;
  const canAddNewContact = noMatchFound && !selectedContact;

  const clearNewContactState = useCallback(() => {
    setShowContactType(false);
    onNewContactBusinessNameChangeRef.current('');
    onNewContactTypeIdChangeRef.current(0);
    onNewContactModeChangeRef.current(false);
  }, []);

  useEffect(() => {
    if (!trimmedTerm) {
      setSearchResults([]);
      setSearchedTerm('');
      return;
    }

    const timeoutId = setTimeout(async () => {
      const term = trimmedTerm;
      setSearchLoading(true);
      try {
        const searchParams = new URLSearchParams();
        searchParams.append('search', term);
        searchParams.append('page', '1');
        searchParams.append('limit', '20');
        searchParams.append('fields', JSON.stringify(['firstName', 'lastName', 'businessName']));

        const response = await contactsApi.getAll(searchParams);
        setSearchResults(response.data);
        setSearchedTerm(term);

        if (response.data.length > 0) {
          setShowContactType(false);
          onNewContactBusinessNameChangeRef.current('');
          onNewContactTypeIdChangeRef.current(0);
          onNewContactModeChangeRef.current(false);
        }
      } catch (error) {
        console.error('Error searching vendors:', error);
        setSearchResults([]);
        setSearchedTerm(term);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [trimmedTerm]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setShowContactType((wasShowing) => {
      if (wasShowing) {
        onNewContactBusinessNameChangeRef.current('');
        onNewContactTypeIdChangeRef.current(0);
        onNewContactModeChangeRef.current(false);
      }
      return false;
    });
  };

  const handleAddNewContact = () => {
    if (!canAddNewContact) {
      return;
    }

    onNewContactBusinessNameChangeRef.current(trimmedTerm);
    setShowContactType(true);
    onNewContactModeChangeRef.current(true);
  };

  const handleSelectContact = (contact: ContactWithDetails) => {
    onContactSelect(contact);
    setSearchTerm('');
    setSearchResults([]);
    setSearchedTerm('');
    clearNewContactState();
  };

  const handleRemoveContact = () => {
    onContactSelect(null);
    clearNewContactState();
  };

  const formatContactLabel = (contact: ContactWithDetails) => {
    const name = `${contact.firstName} ${contact.lastName}`.trim();
    if (contact.businessName) {
      return name ? `${name} (${contact.businessName})` : contact.businessName;
    }
    return name || 'Unknown contact';
  };

  return (
    <div className="expense-vendor-contact-section search-section">
      <div className="app-form-group">
        <label htmlFor="expenseVendorContactSearch" className="app-form-label">
          {label}
        </label>

        {!selectedContact && (
          <div className="search-input-group">
            <div className="expense-vendor-search-input-wrap">
              <input
                id="expenseVendorContactSearch"
                type="text"
                className="app-form-input"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder={placeholder}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                name="expense-vendor-contact-search-field"
              />
              {searchLoading && <div className="search-loading">Searching...</div>}
            </div>
            <button
              type="button"
              className="app-btn app-btn-primary app-btn-small expense-vendor-add-btn"
              onClick={handleAddNewContact}
              title="Add new vendor"
              aria-label="Add new vendor"
              disabled={!canAddNewContact}
            >
              +
            </button>
          </div>
        )}
      </div>

      {selectedContact && (
        <div className="app-form-group selected-items-container">
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 12px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
            borderRadius: '4px',
            marginBottom: '0.5rem'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: '600', color: '#333' }}>
                {formatContactLabel(selectedContact)}
              </div>
            </div>
            <button
              type="button"
              className="app-btn app-btn-danger btn-small"
              onClick={handleRemoveContact}
              title="Remove vendor"
              style={{ marginLeft: '8px' }}
            >
              🗑️
            </button>
          </div>
        </div>
      )}

      {!selectedContact && searchResults.length > 0 && (
        <div className="app-form-group search-results-container">
          <h4 className="search-results-title">Search Results</h4>
          <div className="search-results-list">
            {searchResults.map((contact) => (
              <div key={contact.id} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px',
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#ffffff'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: '600', color: '#333', marginBottom: '4px' }}>
                    {contact.firstName} {contact.lastName}
                  </div>
                  {contact.businessName && (
                    <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                      {contact.businessName}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="app-btn app-btn-primary btn-small"
                  onClick={() => handleSelectContact(contact)}
                  style={{ marginLeft: '8px' }}
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showContactType && !selectedContact && (
        <div className="app-form-group">
          <label htmlFor="newVendorContactTypeId" className="app-form-label required">
            Contact Type
          </label>
          <CategoryDropdown
            parentType="contact"
            value={newContactTypeId}
            onChange={onNewContactTypeIdChange}
            placeholder="Select contact type"
            required={true}
          />
          {errors.typeId && (
            <div className="app-error-message">{errors.typeId}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExpenseVendorContactSection;
